import type { RequestError } from './loadable';

type QueryError = RequestError | undefined;

const SUPPORT_ROLE_MESSAGE = 'You need the support role to see incidents';
const GENERIC_MESSAGE = 'An error occurred while fetching the incidents';
const INCIDENT_UNREADABLE_MESSAGE = 'The incident could not be fetched';

const VERDICT_ROLE_MESSAGE = 'You need the support role to act on incidents';
const ALREADY_CHANGED_MESSAGE =
  'This incident changed before your verdict was recorded — the page now shows where it stands';
const VERDICT_UNRECORDED_MESSAGE = 'The verdict could not be recorded';

const RUN_ROLE_MESSAGE = 'You need the support role to run detection';
const RUN_IN_PROGRESS_MESSAGE = 'A detection run is already in progress';
export const RUN_UNFINISHED_MESSAGE = 'The detection run could not be completed';

const RESET_ROLE_MESSAGE = 'You need the support role to reset diagnostics';
const RESET_DURING_RUN_MESSAGE = 'A detection run is in progress — try again when it finishes.';
const RESET_FAILED_MESSAGE = 'Diagnostics could not be reset';

const ROLE_REFUSAL_STATUSES = [401, 403];
const NOT_FOUND_STATUS = 404;
const CONFLICT_STATUS = 409;

const statusOf = (error: QueryError): number | undefined => {
  const status = error && 'status' in error ? error.status : undefined;

  return typeof status === 'number' ? status : undefined;
};

export const isConflict = (error: QueryError): boolean => statusOf(error) === CONFLICT_STATUS;

const refusesTheRole = (error: QueryError): boolean => {
  const status = statusOf(error);

  return status !== undefined && ROLE_REFUSAL_STATUSES.includes(status);
};

export function incidentsErrorMessage(error: QueryError): string {
  return refusesTheRole(error) ? SUPPORT_ROLE_MESSAGE : GENERIC_MESSAGE;
}

export function incidentDetailErrorMessage(error: QueryError, reference: string): string {
  if (refusesTheRole(error)) {
    return SUPPORT_ROLE_MESSAGE;
  }

  return statusOf(error) === NOT_FOUND_STATUS ? `No incident ${reference}` : INCIDENT_UNREADABLE_MESSAGE;
}

export function verdictErrorMessage(error: QueryError): string {
  if (refusesTheRole(error)) {
    return VERDICT_ROLE_MESSAGE;
  }

  return isConflict(error) ? ALREADY_CHANGED_MESSAGE : VERDICT_UNRECORDED_MESSAGE;
}

export function resetErrorMessage(error: QueryError): string {
  if (refusesTheRole(error)) {
    return RESET_ROLE_MESSAGE;
  }

  return isConflict(error) ? RESET_DURING_RUN_MESSAGE : RESET_FAILED_MESSAGE;
}

export function runErrorMessage(error: QueryError): string {
  if (refusesTheRole(error)) {
    return RUN_ROLE_MESSAGE;
  }

  return isConflict(error) ? RUN_IN_PROGRESS_MESSAGE : RUN_UNFINISHED_MESSAGE;
}
